// ============================================================================
// DashboardDataContext — shares the live, API-backed dashboard view-model with
// the dashboard subcomponents (KPI row, calendar, upcoming, roster, snapshot)
// without prop-drilling. The DashboardPage (and CalendarPage) wrap their tree
// in <DashboardDataProvider/>; components read via useDashboardCtx().
// ============================================================================
import { createContext, useContext, type ReactNode } from "react";
import { localDayKey } from "../lib/date";
import { useDashboardData, type DashboardData } from "./useDashboardData";

const Ctx = createContext<DashboardData | null>(null);

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const data = useDashboardData();
  return <Ctx.Provider value={data}>{children}</Ctx.Provider>;
}

export function useDashboardCtx(): DashboardData {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useDashboardCtx must be used within <DashboardDataProvider>",
    );
  }
  return ctx;
}

/**
 * Sessions on a given LOCAL calendar day, time-sorted. `startsAt` is the
 * device-local wall-clock string (see viewModel.toLocalDateTime), so keying on
 * the local day prefix puts each session on the day the trainer actually sees.
 */
export function sessionsOnDay(
  data: DashboardData,
  day: Date,
): DashboardData["sessions"] {
  const key = localDayKey(day);
  return data.sessions
    .filter((s) => s.startsAt.startsWith(key))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * Provenance-tagged activities on a given LOCAL calendar day, time-sorted. Same
 * local-day keying as sessionsOnDay so own/suggested activities land on the day
 * the trainer sees, merged alongside PT sessions on the calendar grid.
 */
export function activitiesOnDay(
  data: DashboardData,
  day: Date,
): DashboardData["activities"] {
  const key = localDayKey(day);
  return data.activities
    .filter((a) => a.startsAt.startsWith(key))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * Per-day amber meal-summary markers on a given LOCAL calendar day. One marker
 * per client+day (a day-marker carrying that client's total kcal), keyed on the
 * marker's `day` so it lands on the day the trainer sees. Sorted by client name
 * so multiple clients' markers read consistently on the grid.
 */
export function mealMarkersOnDay(
  data: DashboardData,
  day: Date,
): DashboardData["mealMarkers"] {
  const key = localDayKey(day);
  return data.mealMarkers
    .filter((m) => m.day === key)
    .sort((a, b) => (a.clientName ?? "").localeCompare(b.clientName ?? ""));
}

/**
 * Week-level weekly-goal banners whose Monday `week_start` equals the given day's
 * local key. The trainer calendar passes a week-row's first cell (a Monday) so
 * the lime banner spans exactly the weeks that have a coach goal set. Sorted by
 * client name so multiple clients' same-week banners read consistently.
 */
export function weeklyGoalsOnWeek(
  data: DashboardData,
  monday: Date,
): DashboardData["weeklyGoals"] {
  const key = localDayKey(monday);
  return data.weeklyGoals
    .filter((g) => g.weekStart === key)
    .sort((a, b) => (a.clientName ?? "").localeCompare(b.clientName ?? ""));
}
